import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Mixin from 'components/common/Mixin'
import Theme from 'components/common/Theme/Theme'
import Media from 'components/common/Media'
import { A } from 'components/common/Link/Link'
import RoundIcon from 'components/common/RoundIcon/RoundIcon'
import Icon from 'components/common/Icon/Icon'
import Popover from 'components/common/Popover'
import { getOnClickAndOnKeyPressDownEvents } from 'helpers/getOnClickAndOnKeyPressDownEvents'
import { dynamicDataAttribute, toOneString, getDataAtProps } from 'helpers/utils'
import {
  DefaultPopoverLink,
  DefaultPopoverInnerContent,
  DefaultPopoverWrapper } from 'components/common/Popover/elements'

const SummaryField = ({
  className,
  title,
  link,
  isText,
  asset,
  innerProps,
  info,
  small,
  medium,
  setInfoRef,
  setBlockRef,
  hasTooltip,
  editDisabled,
  targetId,
  onClick,
  ...props
}) =>
  <Wrapper className={ className } medium={ medium } { ...dynamicDataAttribute('summary', toOneString(title)) } { ...getDataAtProps(props) }>
    <LeftIcon data-at-left-icon alt="" asset={ asset } small={ small } medium={ medium } inline />
    {/* eslint-disable */}
    <Block data-at-summary-block medium={ medium } ref={ setBlockRef }>
      <Title data-at-title block>{ title }</Title>
      {
        info ?
          <Fragment>
            { editDisabled ?
              <span data-at-summary-info>
                { hasTooltip ?
                  <Popover
                    showOnHover
                    title={ info }
                    info={ info }
                    innerProps={ innerProps }
                    components={{ PopoverLink, PopoverInnerContent, PopoverWrapper }} /> :
                  <InfoText>{ info }</InfoText>
                }
              </span> :
              <Link href={ `#${targetId}` } prevent handler={ onClick }>
                <span data-at-summary-info>
                  { hasTooltip ?
                    <Popover
                      showOnHover
                      title={ info }
                      info={ info }
                      innerProps={ innerProps }
                      components={{ PopoverLink, PopoverInnerContent, PopoverWrapper }} /> :
                    <InfoText
                      { ...getOnClickAndOnKeyPressDownEvents(!editDisabled && onClick) }>
                      { info }
                    </InfoText>
                  }
                </span>
                <Change data-at-summary-change-icon asset="icon-edit" alt="" hidden={ editDisabled } />
              </Link>
            }
            <Media.Desktop>
              <HiddenInfoTextWrapper>
                <HiddenInfoTextForCalculations ref={ setInfoRef } aria-hidden="true">
                  { info }
                </HiddenInfoTextForCalculations>
              </HiddenInfoTextWrapper>
            </Media.Desktop>
          </Fragment> :
        isText ?
          <Placeholder prevent bolder handler={ onClick }>{ link }</Placeholder> :
          <CustomLink
            data-at-custom-link
            href={ `#${targetId}` }
            prevent
            bolder
            handler={ onClick }>{ link }</CustomLink>
      }
      {/* eslint-enable */}
    </Block>
  </Wrapper>

SummaryField.propTypes = {
  asset: PropTypes.string,
  className: PropTypes.string,
  editDisabled: PropTypes.bool,
  hasTooltip: PropTypes.bool,
  info: PropTypes.node,
  innerProps: PropTypes.shape({}),
  isText: PropTypes.bool,
  link: PropTypes.string,
  medium: PropTypes.bool,
  onClick: PropTypes.func,
  setBlockRef: PropTypes.func,
  setInfoRef: PropTypes.func,
  small: PropTypes.bool,
  targetId: PropTypes.string,
  title: PropTypes.string
}

export default SummaryField

const Wrapper = styled.div`
  position: relative;
  ${Mixin.responsive('padding-left', '50px', '0')}
  ${props => props.medium && css`
    ${Mixin.responsive('padding-left', '60px', '0')}
  `}
`

const LeftIcon = styled(RoundIcon)`
  float: left;
  margin-left: -50px;
  ${Mixin.desktopOnly()}
  ${props => props.medium && css`
    margin-left: -60px;
    margin-top: 4px;
  `}
`

const Block = styled.div`
  position: relative;
  vertical-align: middle;
  border-bottom: 1px solid ${Theme.colors.light_grey};
  ${props => props.medium && css`
    border-bottom: 1px solid ${Theme.colors.grey};
  `}
`

const Change = styled(Icon)`
  position: absolute;
  right: 0;
  bottom: 4px;
`

const Link = styled(A)`
  &:hover {
    color: ${Theme.colors.black_new};
  }
`

const Title = styled.div`
  line-height: 19px;
  color: ${Theme.colors.dark_grey};
`

const ellipsisStyling = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const infoTextStyling = css`
  color: ${Theme.colors.black_new};
  ${Mixin.font.GothamBold(18)}
  padding-right: 20px;
`

const InfoText = styled.span`
  ${infoTextStyling}
  ${Media.desktop`
    ${ellipsisStyling}
  `}
`

const PopoverLink = styled(DefaultPopoverLink)`
  display: block;
  cursor: inherit;
  ${infoTextStyling}
  ${ellipsisStyling}
  ${props => props.capitalize && css`
    text-transform: capitalize;
  `}

  &:hover {
    text-decoration: inherit;
  }
`

const HiddenInfoTextWrapper = styled.div`
  display: block;
  height: 0;
  overflow: hidden;
`

const HiddenInfoTextForCalculations = styled(InfoText)`
  opacity: 0;
  line-height: 0;
  display: inline-block;
  white-space: nowrap;
  position: relative;
  z-index: -100;
  padding: 0;
`

const PopoverInnerContent = styled(DefaultPopoverInnerContent)`
  width: 400px;
  ${props => props.capitalize && css`
    text-transform: capitalize;
  `}
`

const PopoverWrapper = styled(DefaultPopoverWrapper)`
  position: relative;
  display: inline;
`

const Placeholder = styled.span`
  color: ${Theme.colors.black_new};
  ${Mixin.font.GothamBold(14)}
`

const CustomLink = styled(A)`
  display: inline-block;
  top: -110px;
  margin-top: 5px;
  line-height: 19px;
`
